import { useState, useEffect } from "react";
import ReusableModal from "../../AppPAges/ManageSettings/ReusableModal";
import useApi from "../../../../utils/api-manager/Helper/useApi";
import { useToastHandler } from "../../../common/ToastHandler";
import useSwal from "../../../common/ReusableComponent/useSwal";
import AssetTable from "../AssetsInventory/AssetTable";
import { ReusableInput } from "../../../common/ReusableComponent/ReusableInput";
import { RegularButton } from "../../../common/ReusableComponent/CustomButton";
import "../Assets.css"
import StatusPill from "../../../common/ReusableComponent/StatusPill";
import ActionButton from "../../../common/ReusableComponent/ActionButton";


const PurchaseOrderManagement = () => {

  const { confirmDelete } = useSwal();
  const { handleToast } = useToastHandler();
  const { GetPaginatedData, Delete, Patch, Get, Post } = useApi();
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [poData, setPoData] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewPoId, setViewPoId] = useState(null);
  const [poDetails, setPoDetails] = useState(null);
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [receivePoItem, setReceivePoItem] = useState(null);
  const [receiveItems, setReceiveItems] = useState([]);
  const [saveLoading, setSaveLoading] = useState(false);

  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceAmount, setInvoiceAmount] = useState("");

  const [hasSpendsTracking, setHasSpendsTracking] = useState(false);
  const checkUserType = async () => {
    try {
      const res = await Get("checkUserType");
      const features = res?.matching_features_titles || [];
      setHasSpendsTracking(features.includes("Spends Tracking"));
    } catch {
      setHasSpendsTracking(false);
    }
  };
  useEffect(() => {
    checkUserType();
  }, []);

  const fetchPOList = async ({ page = 1, searchTerm = "" } = {}) => {
    try {
      setLoading(true);
      const response = await GetPaginatedData("purchaseOrder", {
        page,
        search: searchTerm || undefined,
      });

      const results = response?.data || [];
      setPoData(results);
      setTotalCount(response?.totalCount || 0);
    } catch (error) {
      console.error("Fetch error:", error);
      setPoData([]);
      handleToast({ type: "ERROR", message: "Failed to fetch purchase orders" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchPOList({ page: currentPage, searchTerm: debouncedSearchTerm });
  }, [currentPage, debouncedSearchTerm, refreshTrigger]);

  const handleDelete = async (poId) => {
    const result = await confirmDelete("Are you sure?", "You won't be able to revert this!");
    if (!result.isConfirmed) return;

    try {
      await Delete("purchaseOrder", poId);
      handleToast({ type: "SUCCESS", message: "Purchase Order deleted successfully" });
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      handleToast({ type: "ERROR", message: "Failed to delete purchase order" });
    }
  };


  const fetchPoById = async () => {
    if (!viewPoId) return;

    try {
      setLoading(true);

      const res = await Get("purchaseOrder");
      const po = res.results.find(p => p.id === viewPoId);

      if (!po) {
        throw new Error("PO not found");
      }

      setPoDetails(po);
    } catch (err) {
      handleToast({
        type: "ERROR",
        message: "Failed to fetch PO details"
      });
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    if (showViewModal && viewPoId) {
      fetchPoById();
    }
  }, [showViewModal, viewPoId]);
  /*============================Receive Variance================*/
  const initializeReceiveItems = (poItems) => {
    return poItems.map(item => ({
      id: item.id,
      product_name: item.product_name,
      specification: item.specification,
      qty: item.ordered_quantity,
      uom_title: item.uom_title,
      received_qty: 0,
      damaged_qty: 0,
      excess_qty: 0,
      short_qty: 0,
      is_verified: false
    }));
  };



  const handleReceiveItemChange = (itemId, field, value) => {
  setReceiveItems(prev =>
    prev.map(item => {
      if (item.id !== itemId) return item;

      const ordered = Number(item.qty) || 0;

      if (field === "is_verified") {
        return { ...item, is_verified: value };
      }

      const numericValue = Math.max(0, Number(value) || 0);

      if (field === "received_qty") {
        let excess = 0;
        let short = 0;

        if (numericValue > ordered) {
          excess = numericValue - ordered;
        } else if (numericValue < ordered) {
          short = ordered - numericValue;
        }

        return {
          ...item,
          received_qty: numericValue,
          excess_qty: excess,
          short_qty: short
        };
      }

      return { ...item, [field]: numericValue };
    })
  );
};


  const handleAssetReceiveSave = async () => {
    setSaveLoading(true);
    try {
      const payload = {
        status: "delivered",
        invoice_number: invoiceNumber,
        invoice_amount: Number(invoiceAmount) || 0,
        po_items: receiveItems.map(item => ({
          id: item.id,
          received_qty: item.received_qty,
          damaged_qty: item.damaged_qty,
          excess_qty: item.excess_qty,
          short_qty: item.short_qty,
          is_verified: item.is_verified
        }))
      };

      await Patch("purchaseOrder", receivePoItem.id, payload);

      if (hasSpendsTracking) {
        if (!invoiceNumber || !invoiceAmount) {
          handleToast({
            type: "ERROR",
            message: "Invoice number and amount required"
          });
          return;
        }

        const invoicePayload = {
          type: "expense",
          store: receivePoItem.store_id,
          invoice_no: invoiceNumber,
          date: new Date().toISOString().slice(0, 10),
          status: "unpaid",
          pay_method_status: "pay_later",
          pay_method: "pay_later",
          amount: String(invoiceAmount),
          pay_amount: null,
          discount: null,
          remaining_amount: String(invoiceAmount),
          after_discount: null,
          prepaid_tax: false,
          prepaid_tax_percentage: "",
          vendor_department: receivePoItem.vendor,
          vendor_department_name: receivePoItem.vendor_name,
          vendor_department_service_titles: receivePoItem.vendor_department_name,

          cheque_no: "",
          note: "",
          // due_date: receivePoItem.expected_delivery_date,
          bank: "",
          days: "0"
        };

        await Post("createInvoiceAndPrepaidTax", invoicePayload);
      }
      handleToast({ type: "SUCCESS", message: "Assets received successfully" });
      setShowReceiveModal(false);
      setRefreshTrigger(p => p + 1);
    } catch (error) {
      handleToast({
        type: "ERROR",
        message: error?.response?.data?.message || "Failed to receive assets"
      });
    } finally {
      setSaveLoading(false);
    }
  };

  const PO_STATUS_PILL = {
    issued: {
      bg: "#fff7ed",
      text: "#c2410c",
      border: "#fed7aa",
    },
    delivered: {
      bg: "#ecfeff",
      text: "#0f766e",
      border: "#99f6e4",
    },
    completed: {
      bg: "#f0fdf4",
      text: "#166534",
      border: "#bbf7d0",
    },
    cancelled: {
      bg: "#fdecec",
      text: "#e11d48",
      border: "#fda4af",
    },
  };
  const BTN = {
    receive: {
      bg: "#ecfeff",
      text: "#0891b2",
      border: "#bae6fd",
      hover: "#cffafe",
    },
    view: {
      bg: "#eef2ff",
      text: "#4338ca",
      border: "#c7d2fe",
      hover: "#e0e7ff",
    },
  };

  const columns = [
    { header: "PO Number", field: "po_number" },
    { header: "Vendor", field: "vendor_name" },
    // { header: "Total Amount", render: (row) => `$${parseFloat(row.total_amount || 0).toFixed(2)}` },
    { header: "Invoice Number", field:"invoice_number" },
     { header: "Invoice Amount", field:"invoice_amount" },
    {
      header: "Status",
      field: "status",
      render: (row) => {
        const cfg = PO_STATUS_PILL[row.status] || PO_STATUS_PILL.issued;

        return (
          <StatusPill
            label={row.status.replace(/_/g, " ")}
            bgColor={cfg.bg}
            textColor={cfg.text}
            borderColor={cfg.border}
          />
        );
      }

    },
    {
      header: "Receive Asset",
      render: (row) => {
        if (row.status !== "issued") {
          return "-";
        }

        return (
          <ActionButton
            onClick={() => {
              setReceivePoItem(row);
              setReceiveItems(initializeReceiveItems(row.items || []));
              setShowReceiveModal(true);
            }}
            bgColor={BTN.receive.bg}
            textColor={BTN.receive.text}
            borderColor={BTN.receive.border}
            hoverBgColor={BTN.receive.hover}
          >
            Receive Assets
          </ActionButton>
        );
      }
    },
    {
      header: "View",
      render: (row) => (
        <ActionButton
          onClick={() => {
            setViewPoId(row.id);
            setShowViewModal(true);
          }}
          bgColor={BTN.view.bg}
          textColor={BTN.view.text}
          borderColor={BTN.view.border}
          hoverBgColor={BTN.view.hover}
        >
          View
        </ActionButton>

      )
    }

  ];

  return (
    <div className="responsive-box">
      <div className="inventory-header">
        <div className="inventory-title">Purchase Order Management</div>
        <div className="btn-position">
        </div>

      </div>
      <ReusableModal
        title="Purchase Order Details"
        show={showViewModal}
        handleClose={() => {
          setShowViewModal(false);
          setViewPoId(null);
          setPoDetails(null);
        }}
        width="1200px"
      // width="800px"
      >
        {loading && <p>Loading...</p>}

        {poDetails && (
          <>
            <div className="po-view-container">
            </div>
            <div className="po-view-meta">
              <div className="po-meta-left">
                <div className="po-meta-item">
                  <span className="po-meta-label">PO Number</span>
                  <span className="po-meta-value">{poDetails.po_number}</span>
                </div>

                <div className="po-meta-item">
                  <span className="po-meta-label">Vendor</span>
                  <span className="po-meta-value">{poDetails.vendor_name}</span>
                </div>

                <div className="po-meta-item">
                  <span className="po-meta-label">Status</span>
                  <span className={`po-status-badge po-status-${poDetails.status}`}>
                    {poDetails.status?.replace(/_/g, " ").toUpperCase()}
                  </span>
                </div>
              </div>

              <div className="po-meta-right">
                <div className="po-meta-item">
                  <span className="po-meta-label">RFQ Name</span>
                  <span className="po-meta-value">
                    {poDetails.rfq_name || "N/A"}
                  </span>
                </div>

                <div className="po-meta-item">
                  <span className="po-meta-label">RFQ Number</span>
                  <span className="po-meta-value">
                    {poDetails.rfq_number || "N/A"}
                  </span>
                </div>

              </div>
              <div className="po-meta-item">
                <span className="po-meta-label">Invoice Number</span>
                <span className="po-meta-value">
                  {poDetails.invoice_number || "N/A"}
                </span>
              </div>

              <div className="po-meta-item">
                <span className="po-meta-label">Invoice Amount</span>
                <span className="po-meta-value">
                  ₹ {poDetails.invoice_amount || "0.00"}
                </span>
              </div>

            </div>
            <div className="po-items-section">
              <div className="po-items-header">
                <span>PRODUCT</span>
                <span>SPECIFICATION</span>
                <span className="po-items-qty">ORDERED</span>
                <span className="po-items-qty">RECEIVED</span>
                <span className="po-items-qty">DAMAGED</span>
                <span className="po-items-qty">EXCESS</span>
                <span className="po-items-qty">SHORT</span>
              </div>


              {poDetails.items.map(item => (
                <div className="po-items-row" key={item.id}>
                  <span>{item.product_name}</span>
                  <span>{item.specification}</span>

                  <span className="po-items-qty">
                    {item.ordered_quantity ?? 0}
                  </span>

                  <span className="po-items-qty">
                    {item.received_qty ?? 0}
                  </span>

                  <span className="po-items-qty">
                    {item.damaged_qty ?? 0}
                  </span>

                  <span className="po-items-qty">
                    {item.excess_qty ?? 0}
                  </span>

                  <span className="po-items-qty">
                    {item.short_qty ?? 0}
                  </span>
                </div>
              ))}

            </div>

          </>
        )}
      </ReusableModal>

      {/* /*========================Receive Variance========================*/}

      <ReusableModal
        title="Receive Variance"
        show={showReceiveModal}
        handleClose={() => {
          setShowReceiveModal(false);
          setReceivePoItem(null);
        }}
        width="1200px"
      >
        {receivePoItem && (
          <div className="rv-po-info">
            <div className="rv-po-info-item">
              <span className="rv-label">PO Number</span>
              <span className="rv-value">{receivePoItem.po_number}</span>
            </div>
            <div className="rv-po-info-item">
              <span className="rv-label">RFQ Number</span>
              <span className="rv-value">{receivePoItem.rfq_number}</span>
            </div>
            <div className="rv-po-info-item">
              <span className="rv-label">RFQ Name</span>
              <span className="rv-value">{receivePoItem.rfq_name}</span>
            </div>
            <div className="rv-po-info-item">
              <span className="rv-label">Vendor</span>
              <span className="rv-value">{receivePoItem.vendor_name}</span>
            </div>
          </div>
        )}
        <div className="rv-invoice-section">
          <div className="rv-invoice-item">
            {/* <span className="rv-label">Invoice Number</span> */}
            <ReusableInput
              type="text"
              label="Invoice Number"
              value={invoiceNumber}
              onChange={e => setInvoiceNumber(e.target.value)}
              // placeholder="Enter invoice number"
              style={{ width: "250px" }}
            />
          </div>

          {/* <div className="rv-invoice-item">
         
            <ReusableInput
              type="number"
              label="Invoice Amount"
              value={invoiceAmount}
              onChange={e => setInvoiceAmount(e.target.value)}
              style={{ width: "250px" }}
            />
          </div> */}
          <ReusableInput
            type="number"
            label="Invoice Amount"
            min={0}
            value={invoiceAmount}
            onChange={e =>
              setInvoiceAmount(Math.max(0, Number(e.target.value) || 0))
            }
            style={{ width: "250px" }}
          />


        </div>
        <div className="receive-variance-table">
          <div className="receive-variance-header">
            <span>ITEM</span>
            <span className="rv-center">ORDERED</span>
            <span className="rv-center">RECEIVED</span>
            <span className="rv-center">DAMAGED</span>
            <span className="rv-center">EXCESS</span>
            <span className="rv-center">SHORT</span>
            <span className="rv-center">VERIFY</span>
          </div>

          {receiveItems.map(item => (
            <div className="receive-variance-row" key={item.id}>
              <span>{item.product_name}</span>
              <span className="rv-center">{item.qty}</span>

              <ReusableInput
                type="number"
                value={item.received_qty}
                onChange={e =>
                  handleReceiveItemChange(item.id, "received_qty", e.target.value)
                }
                style={{ width: "80px" }}
              />

              <ReusableInput
                type="number"
                value={item.damaged_qty}
                onChange={e =>
                  handleReceiveItemChange(item.id, "damaged_qty", e.target.value)
                }
                style={{ width: "80px" }}
              />

              <ReusableInput
                type="number"
                min={0}
                value={item.excess_qty}
                onChange={e =>
                  handleReceiveItemChange(item.id, "excess_qty", e.target.value)
                }
                style={{ width: "80px" }}
              />

              <ReusableInput
                type="number"
                value={item.short_qty}
                onChange={e =>
                  handleReceiveItemChange(item.id, "short_qty", e.target.value)
                }
                style={{ width: "80px" }}
              />

              <input
                type="checkbox"
                checked={item.is_verified}
                onChange={e =>
                  handleReceiveItemChange(item.id, "is_verified", e.target.checked)
                }
              />
            </div>
          ))}

          <div className="rv-footer">
            <RegularButton
              text={saveLoading ? "Saving..." : "Save All Items"}
              onClick={handleAssetReceiveSave}
              backgroundColor="#282e26"
              disabled={saveLoading}
            />
          </div>
   
        </div>
      </ReusableModal>


      <AssetTable
        data={poData}
        columns={columns}
        loading={loading}
        showAction={true}
        onDelete={handleDelete}
        showFooter
        currentPage={currentPage}
        totalCount={totalCount}
        onPageChange={setCurrentPage}
        searchTerm={searchTerm}
        onSearch={setSearchTerm}
      />
    </div>
  );
};

export default PurchaseOrderManagement;